import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuButton, MenuItem, MenuList } from '@reach/menu-button';
import { ReactComponent as ActiveNotificationBell } from 'components/icons/active-notifications-bell.svg';
import { ReactComponent as NotificationsBell } from 'components/icons/notifications-bell.svg';
import { Button, Flex, Text } from '@resideo/blueprint-react';
import { SuccessAlert, ErrorAlert } from 'components/common/Alert';
import styled from 'styled-components';
import { useToast } from 'components/common/Toast';
import { useIsMounted } from 'hooks/useIsMounted';
import { graphql } from 'babel-plugin-relay/macro';
import { useRelayEnvironment, useMutation } from 'relay-hooks';
import { fetchQuery } from 'relay-runtime';
import {
  NotificationsQuery,
  NotificationsQueryResponse,
} from './__generated__/NotificationsQuery.graphql';
import {
  NotificationsActivateUserMutation,
  NotificationsActivateUserMutationResponse,
} from './__generated__/NotificationsActivateUserMutation.graphql';

const StyledMenuButton = styled(MenuButton)`
  cursor: pointer;
`;

const NotificationRowComponent = ({ node, fetch }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const isMounted = useIsMounted();

  const [activateUser] = useMutation<NotificationsActivateUserMutation>(graphql`
    mutation NotificationsActivateUserMutation($id: ID!) {
      inviteAccept(id: $id) {
        invite {
          id
        }
        errors {
          message
        }
      }
    }
  `);

  const acceptInvite = async () => {
    await activateUser({
      variables: {
        id: node.id,
      },
      onError: () => {
        toast(ErrorAlert(t), { duration: 10000 });
      },
      onCompleted: (response: NotificationsActivateUserMutationResponse) => {
        const seviceErrors = response?.inviteAccept?.errors;

        if (!seviceErrors) {
          if (isMounted()) {
            fetch();
          }
          toast(SuccessAlert(t, 'header.notifications.toast.success'));
        } else {
          toast(ErrorAlert(t));
        }
      },
    });
  };

  return (
    <MenuItem
      onSelect={() => {
        acceptInvite();
      }}>
      <Flex
        justifyContent='space-between'
        flexDirection={['column', 'row']}
        data-test-invite-notification>
        <Flex
          flexDirection='column'
          marginRight='large'
          alignItems='self-start'
          paddingBottom={['medium', 0]}>
          <Text color='white'>
            {t('header.notifications.invitedTo')}
            {` ${node.partnerAccount.name}`}
          </Text>
          <Text color='faded' fontSize='small'>
            {t('header.notifications.invitedBy')}
          </Text>
        </Flex>
        <Button>{t('header.notifications.acceptInvite')}</Button>
      </Flex>
    </MenuItem>
  );
};

const CustomMenuList = styled(MenuList)`
  position: fixed;
  right: 0;
  top: 50px;
  @media (min-width: ${({ theme }) => theme.breakpoints.large}) {
    right: 70px;
    top: 50px;
    width: auto;
  }
`;

const useMountEffect = (cb: () => void) => useEffect(cb, []);

const Notifications: FC = () => {
  const isMounted = useIsMounted();
  const environment = useRelayEnvironment();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<
    NotificationsQueryResponse
  >();

  const notificationsQuery = graphql`
    query NotificationsQuery {
      viewer {
        id
        partnerUsers {
          edges {
            node {
              ... on PartnerUser {
                status
                role
                invites {
                  edges {
                    node {
                      id
                      status
                      expiresAt
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const fetchNotifications = () => {
    if (environment === null) throw new Error('No relay environment set');

    fetchQuery<NotificationsQuery>(
      environment,
      notificationsQuery,
      {}
    ).subscribe({
      next: response => {
        if (isMounted()) {
          setNotifications(response);
        }
      },
      error: () => null,
    });
  };

  useMountEffect(fetchNotifications);

  const notExpired = edge => {
    const now = Date.now();
    const currentExpiration = new Date(edge.node?.expirationTime || 0);
    return currentExpiration.getTime() > now;
  };

  const invites = (notifications?.viewer?.partnerUsers?.edges || []).filter(
    notExpired
  );

  return (
    <Flex marginRight='medium' data-test-notifications-menu>
      <Menu>
        <StyledMenuButton onClick={fetchNotifications}>
          {invites.length > 0 ? (
            <ActiveNotificationBell />
          ) : (
            <NotificationsBell />
          )}
        </StyledMenuButton>
        {invites.length > 0 ? (
          <CustomMenuList portal={false}>
            {invites.map((notification, idx) => (
              <NotificationRowComponent
                fetch={fetchNotifications}
                key={idx as any}
                node={notification?.node}
              />
            ))}
          </CustomMenuList>
        ) : (
          <CustomMenuList portal={false}>
            <Text marginX='large' color='white' data-test-no-notifications>
              {t('header.notifications.emptyState')}
            </Text>
          </CustomMenuList>
        )}
      </Menu>
    </Flex>
  );
};

export default Notifications;
